import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Database,
  Download,
  RotateCcw,
  HardDrive,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Server,
  Layers,
  HelpCircle,
  FileCode2,
  RefreshCw,
  Zap,
  Upload,
  FileJson,
  Check,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

interface DatabaseBackupManagerProps {
  primaryColor?: string;
}

export default function DatabaseBackupManager({ primaryColor = '#059669' }: DatabaseBackupManagerProps) {
  const { showToast, refreshData } = useApp();
  const [stats, setStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isVacuuming, setIsVacuuming] = useState(false);
  const [vacuumMessage, setVacuumMessage] = useState<string | null>(null);

  // Restore States
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreMode, setRestoreMode] = useState<'replace' | 'merge'>('replace');
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreSuccessMsg, setRestoreSuccessMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const res = await fetch('/api/database/stats');
      const data = await res.json();
      if (data.success) {
        setStats(data.stats || data.data);
      }
    } catch (err) {
      console.error('Failed to load database stats:', err);
    }
    setIsLoadingStats(false);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleVacuumDatabase = async () => {
    setIsVacuuming(true);
    setVacuumMessage(null);
    try {
      const res = await fetch('/api/database/vacuum', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setVacuumMessage('Database SQLite berhasil dibersihkan & di-VACUUM! Ruang kosong yang tidak terpakai sudah dipangkas.');
        showToast('Optimasi SQLite VACUUM sukses!', 'success');
        fetchStats();
      } else {
        setVacuumMessage(data.message || 'Optimasi selesai');
      }
    } catch (err) {
      showToast('Gagal menjalankan optimasi database', 'error');
    }
    setIsVacuuming(false);
  };

  const handleDownloadSql = () => {
    window.open('/api/database/export/sql', '_blank');
    showToast('Mengunduh SQL Dump (.sql)...', 'info');
  };

  const handleDownloadJson = () => {
    window.open('/api/database/export/json', '_blank');
    showToast('Mengunduh Full Backup JSON (.json)...', 'info');
  };

  const handleDownloadSqlite = () => {
    window.open('/api/database/export/sqlite', '_blank');
    showToast('Mengunduh File SQLite Asli (.sqlite)...', 'info');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.json')) {
        showToast('Harap pilih file cadangan berformat .json', 'warning');
        return;
      }
      setRestoreFile(file);
      setRestoreSuccessMsg(null);
    }
  };

  const handleExecuteRestore = async () => {
    if (!restoreFile) {
      showToast('Pilih file cadangan JSON terlebih dahulu', 'warning');
      return;
    }

    const confirmMsg =
      restoreMode === 'replace'
        ? 'PERINGATAN: Mode "Timpa Seluruh Data" akan menggantikan seluruh konten aktif dengan isi file cadangan. Lanjutkan?'
        : 'Mode "Gabungkan Data" akan menambahkan konten dari file cadangan ke data yang ada tanpa menghapus data aktif. Lanjutkan?';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsRestoring(true);
    setRestoreSuccessMsg(null);

    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('mode', restoreMode);

      const res = await fetch('/api/database/restore', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Gagal memulihkan database dari file cadangan');
      }

      setRestoreSuccessMsg(data.message);
      showToast(data.message || 'Database berhasil dipulihkan!', 'success');
      setRestoreFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh app data and stats
      await refreshData();
      await fetchStats();
    } catch (err: any) {
      console.error('Database restore error:', err);
      showToast(err.message || 'Gagal memulihkan data', 'error');
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-600" />
            <span>Pusat Manajemen Database & Cadangan Data</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Cadangkan data website, unduh file SQL dump untuk cPanel / phpMyAdmin, dan pulihkan data kapan saja secara mandiri.
          </p>
        </div>

        <button
          onClick={fetchStats}
          disabled={isLoadingStats}
          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 self-start shrink-0 cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoadingStats ? 'animate-spin' : ''}`} />
          <span>Segarkan Metrik</span>
        </button>
      </div>

      {/* Database Live Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl border border-slate-200 bg-emerald-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-800">Ukuran File SQLite</span>
            <HardDrive className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="text-2xl font-extrabold text-emerald-950 mt-1">
            {stats ? stats.sqliteFileSizeFormatted : 'Memuat...'}
          </p>
          <span className="text-[10px] text-emerald-700 font-medium">Sangat ringan & hemat server</span>
        </div>

        <div className="p-4 rounded-2xl border border-slate-200 bg-blue-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-blue-800">Total Artikel & Berita</span>
            <Layers className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-2xl font-extrabold text-blue-950 mt-1">
            {stats?.counts ? stats.counts.articles : 0}
          </p>
          <span className="text-[10px] text-blue-700 font-medium">Postingan aktif</span>
        </div>

        <div className="p-4 rounded-2xl border border-slate-200 bg-purple-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-purple-800">Produk & Galeri</span>
            <Server className="w-4 h-4 text-purple-600" />
          </div>
          <p className="text-2xl font-extrabold text-purple-950 mt-1">
            {stats?.counts ? (stats.counts.products || 0) + (stats.counts.gallery || 0) : 0}
          </p>
          <span className="text-[10px] text-purple-700 font-medium">Katalog & media aktif</span>
        </div>

        <div className="p-4 rounded-2xl border border-slate-200 bg-amber-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-amber-800">Format Kompresi Media</span>
            <Zap className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-2xl font-extrabold text-amber-950 mt-1">WebP Otomatis</p>
          <span className="text-[10px] text-amber-700 font-medium">Kompresi kanvas hemat bandwidth</span>
        </div>
      </div>

      {vacuumMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{vacuumMessage}</span>
        </div>
      )}

      {/* Database Maintenance & Backup Tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tool 1: SQLite VACUUM Optimization */}
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col justify-between space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                <RefreshCw className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-slate-900">Bersihkan & Padatkan SQLite (VACUUM)</h3>
                <span className="text-[11px] text-slate-500">Kompilasi ulang database dan buang sisa data terhapus</span>
              </div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed mt-3">
              Perintah ini mengoptimalkan seluruh database, mengembalikan ruang disk yang tidak terpakai dari artikel/media yang pernah dihapus, sehingga database tetap seringan mungkin.
            </p>
          </div>

          <button
            disabled={isVacuuming}
            onClick={handleVacuumDatabase}
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-xs transition-colors cursor-pointer"
          >
            <RotateCcw className={`w-4 h-4 ${isVacuuming ? 'animate-spin' : ''}`} />
            <span>{isVacuuming ? 'Sedang Memadatkan Database...' : 'Jalankan VACUUM Sekarang'}</span>
          </button>
        </div>

        {/* Tool 2: Download Backup Mandiri (SQL / JSON / SQLite) */}
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col justify-between space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                <Download className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-slate-900">Unduh Cadangan Data Mandiri</h3>
                <span className="text-[11px] text-slate-500">Cadangkan seluruh artikel, tema, produk & konfigurasi</span>
              </div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed mt-3">
              Pilih format cadangan sesuai kebutuhan Anda: <strong>.SQL</strong> untuk phpMyAdmin / MySQL cPanel, <strong>.JSON</strong> untuk pemulihan cepat, atau <strong>.SQLITE</strong> untuk file database langsung.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              onClick={handleDownloadSql}
              className="py-2.5 px-2 rounded-xl text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs transition-colors cursor-pointer"
              style={{ backgroundColor: primaryColor }}
              title="Unduh SQL Dump untuk phpMyAdmin / MySQL"
            >
              <FileCode2 className="w-4 h-4 shrink-0" />
              <span>Unduh .SQL</span>
            </button>

            <button
              onClick={handleDownloadJson}
              className="py-2.5 px-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition-colors cursor-pointer"
              title="Unduh Full Backup JSON untuk restore"
            >
              <FileJson className="w-4 h-4 shrink-0" />
              <span>Unduh .JSON</span>
            </button>

            <button
              onClick={handleDownloadSqlite}
              className="py-2.5 px-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition-colors cursor-pointer"
              title="Unduh File SQLite Asli (.sqlite)"
            >
              <Database className="w-4 h-4 shrink-0" />
              <span>Unduh .SQLITE</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tool 3: Pulihkan Data dari File Cadangan (Restore JSON) */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-7 shadow-2xs space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center font-black">
            <Upload className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-extrabold text-slate-900 text-base">
              Pulihkan Data dari File Cadangan (Restore JSON)
            </h3>
            <p className="text-xs text-slate-500">
              Unggah file backup <code>.json</code> yang pernah Anda unduh untuk memulihkan seluruh konten, artikel, produk, portofolio, dan pengaturan website.
            </p>
          </div>
        </div>

        {restoreSuccessMsg && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs text-emerald-800 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-emerald-900">Pemulihan Sukses!</p>
              <p className="mt-0.5">{restoreSuccessMsg}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
          {/* File Picker */}
          <div className="md:col-span-7">
            <label className="block text-xs font-bold text-slate-700 mb-2">
              Pilih File Cadangan (.json)
            </label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
                id="db-restore-file"
              />
              <label
                htmlFor="db-restore-file"
                className="flex-1 py-3 px-4 border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-2xl text-xs font-bold text-slate-700 hover:text-emerald-700 flex items-center justify-center gap-2 cursor-pointer transition-colors bg-slate-50/50 hover:bg-emerald-50/30"
              >
                <FileJson className="w-4 h-4 text-emerald-600" />
                <span className="truncate">
                  {restoreFile ? restoreFile.name : 'Klik untuk memilih file .json backup'}
                </span>
              </label>

              {restoreFile && (
                <button
                  type="button"
                  onClick={() => {
                    setRestoreFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="px-3 py-2 text-xs font-bold text-slate-400 hover:text-rose-600 rounded-xl"
                >
                  Batal
                </button>
              )}
            </div>
            {restoreFile && (
              <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1 font-medium">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span>Ukuran file: {(restoreFile.size / 1024).toFixed(1)} KB</span>
              </p>
            )}
          </div>

          {/* Mode Selector */}
          <div className="md:col-span-5 space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Metode Pemulihan
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRestoreMode('replace')}
                className={`py-2 px-3 rounded-xl border text-xs font-bold text-left transition-all cursor-pointer ${
                  restoreMode === 'replace'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-500/20'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span>Timpa Penuh</span>
                  {restoreMode === 'replace' && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                </div>
                <p className="text-[10px] text-slate-500 font-normal">Gantikan semua data aktif</p>
              </button>

              <button
                type="button"
                onClick={() => setRestoreMode('merge')}
                className={`py-2 px-3 rounded-xl border text-xs font-bold text-left transition-all cursor-pointer ${
                  restoreMode === 'merge'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-500/20'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span>Gabungkan</span>
                  {restoreMode === 'merge' && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                </div>
                <p className="text-[10px] text-slate-500 font-normal">Tambahkan ke data aktif</p>
              </button>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-100">
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <span>Disarankan mengunduh backup terkini terlebih dahulu sebelum melakukan pemulihan.</span>
          </p>

          <button
            type="button"
            disabled={!restoreFile || isRestoring}
            onClick={handleExecuteRestore}
            className="w-full sm:w-auto px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer shrink-0"
          >
            {isRestoring ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Memulihkan Database...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Mulai Pulihkan Database Sekarang</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Step-by-Step Guide for cPanel Backup */}
      <div className="p-5 bg-white rounded-2xl border border-slate-200 space-y-3">
        <div className="flex items-center gap-2 font-bold text-sm text-slate-900">
          <HelpCircle className="w-4 h-4 text-emerald-600" />
          <span>Panduan Praktis Backup & Restore di cPanel Hosting:</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600">
          <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 space-y-1.5">
            <h5 className="font-bold text-slate-800 flex items-center gap-1">
              <span>1. Cara Backup Manual di cPanel:</span>
            </h5>
            <ol className="list-decimal pl-4 space-y-1 text-[11px] leading-relaxed">
              <li>Klik tombol <strong>Unduh .SQL</strong> atau <strong>Unduh .JSON</strong> di atas.</li>
              <li>Buka <strong>cPanel &rarr; File Manager</strong> &rarr; folder website Anda.</li>
              <li>Klik kanan folder <code>dist</code> atau root &rarr; pilih <strong>Compress (ZIP)</strong>.</li>
              <li>Simpan file ZIP di komputer Anda sebagai arsip cadangan aman.</li>
            </ol>
          </div>

          <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 space-y-1.5">
            <h5 className="font-bold text-slate-800 flex items-center gap-1">
              <span>2. Cara Restore / Pindah Server:</span>
            </h5>
            <ol className="list-decimal pl-4 space-y-1 text-[11px] leading-relaxed">
              <li>Ekstrak file ZIP di File Manager hosting tujuan Anda.</li>
              <li>Jika menggunakan database MySQL: buka <strong>phpMyAdmin &rarr; Import</strong> &rarr; pilih file <code>.sql</code> tadi.</li>
              <li>Atau gunakan fitur <strong>Pulihkan Data (.JSON)</strong> di atas untuk memulihkan seluruh artikel & pengaturan secara instan.</li>
              <li>Jalankan <code>npm start</code> atau restart Node.js App di cPanel Anda.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
